import React, { type PropsWithoutRef, type RefAttributes, useRef } from "react";

const forwardRefSymbol = Symbol.for("react.forward_ref");

const componentType = (Component: unknown) => {
  // empty
  if (!Component) return "null";

  // html tag component
  if (typeof Component === "string") return "string";

  // class component
  if (
    typeof Component === "function" &&
    Component.prototype instanceof React.Component
  )
    return "class";

  // forwardref component
  if (
    typeof Component === "object" &&
    (Component as React.ExoticComponent).$$typeof === forwardRefSymbol
  )
    return "forwardref";

  // functional component
  return "function";
};

const componentsMatch = (a: unknown, b: unknown) => {
  const typeA = componentType(a);
  const typeB = componentType(b);
  if (typeA === "function" && typeB === "function") {
    // compare functions or function strings
    // if the function strings match, the passed component (most likely) didn't change
    return a === b || "" + a === "" + b;
  }
  if (typeA === "forwardref" && typeB === "forwardref") {
    // compare functions or function strings
    // if the function strings match, the passed component (most likely) didn't change
    const renderA = (a as { render: unknown }).render;
    const renderB = (b as { render: unknown }).render;
    return renderA === renderB || "" + renderA === "" + renderB;
  }
  return typeA === typeB;
};

interface NestedComponentState<P> {
  Component: React.ElementType<P> | string | null;
  StableComponent: React.FC<P>;
}

const makeStableComponent = <P extends object>(
  state: React.MutableRefObject<NestedComponentState<P>>
): React.FC<P> => {
  return (props: P): React.ReactNode => {
    const { Component } = state.current;

    switch (componentType(Component)) {
      case "null": {
        // nothing; return null
        return null;
      }
      case "string": {
        // just a string; no concerns rendering directly
        const Comp = Component as React.ComponentType<P>;
        return <Comp {...props} />;
      }
      case "class": {
        // class form; chances are, this component's declaration isn't nested
        const Comp = Component as React.ComponentType<P>;
        return <Comp {...props} />;
      }
      case "forwardref": {
        // forwardref component
        const component = Component as unknown as {
          render?: (
            props: P,
            ref: React.ForwardedRef<unknown>
          ) => React.ReactNode;
        };
        return component.render?.(props, null);
      }
      case "function": {
        // functional form; call vs. render:
        const component = Component as React.FC<P>;
        return component(props);
      }
    }
  };
};

export function useNestedComponent(Component: undefined | null): null;
export function useNestedComponent<P extends object>(
  Component: React.ElementType<P> | string
): React.FC<P>;
export function useNestedComponent<P extends object>(
  Component: React.ElementType<P> | string | undefined | null
): React.FC<P> | null;

/** Allows safely using props as components. Also allows declaring components within a render function. */
export function useNestedComponent<P extends object>(
  Component: React.ElementType<P> | string | undefined | null
): React.FC<P> | null {
  const state = useRef<NestedComponentState<P>>({
    Component: null,
    StableComponent: null!,
  });

  if (Component) {
    // if type of component changes (e.g., from null to functional, functional to string, etc)...
    if (!componentsMatch(state.current.Component, Component)) {
      // ...switch out StableComponent since hooks will likely mismatch
      state.current.StableComponent = makeStableComponent<P>(state);
    }
    state.current.Component = Component;
    return state.current.StableComponent;
  }
  return null;
}

///////////////////////////////

type ForwardRefComponent<Ref, P> = React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<React.ElementType<P>> & RefAttributes<Ref>
>;

interface NestedRefComponentState<Ref, P> {
  Component: React.ElementType<P> | string | null;
  StableComponent: ForwardRefComponent<Ref, P>;
}

const makeStableComponentWithRef = <Ref, P extends object>(
  state: React.MutableRefObject<NestedRefComponentState<Ref, P>>
): ForwardRefComponent<Ref, P> => {
  return React.forwardRef((props: PropsWithoutRef<P>, forwardedRef) => {
    const { Component } = state.current;

    switch (componentType(Component)) {
      case "null": {
        // nothing; return null
        return null;
      }
      case "string": {
        // just a string; no concerns rendering directly
        const Comp = Component as ForwardRefComponent<unknown, object>;
        return <Comp {...props} ref={forwardedRef} />;
      }
      case "class": {
        // class form; chances are, this component's declaration isn't nested
        const Comp = Component as React.ComponentType<P>;
        return <Comp {...(props as P)} />;
      }
      case "forwardref": {
        // forwardref component
        const component = Component as unknown as {
          render?: (props: P, ref: React.ForwardedRef<Ref>) => React.ReactNode;
        };
        return component.render?.(props as P, forwardedRef);
      }
      case "function": {
        // functional form; call vs. render:
        const component = Component as React.FC;
        return component(props, forwardedRef);
      }
    }
  }) as ForwardRefComponent<Ref, P>;
};

export function useNestedComponentWithRef(Component: undefined | null): null;
export function useNestedComponentWithRef<Ref, P extends object>(
  Component: React.ElementType<P> | string
): ForwardRefComponent<Ref, P>;
export function useNestedComponentWithRef<Ref, P extends object>(
  Component: React.ElementType<P> | string | undefined | null
): ForwardRefComponent<Ref, P> | null;

/** Allows safely using props as components. Also allows declaring components within a render function. A version that forwards refs. */
export function useNestedComponentWithRef<Ref, P extends object>(
  Component: React.ElementType<P> | string | undefined | null
): ForwardRefComponent<Ref, P> | null {
  const state = useRef<NestedRefComponentState<Ref, P>>({
    Component: null,
    StableComponent: null!,
  });

  if (Component) {
    // if type of component changes (e.g., from null to functional, functional to string, etc)...
    if (!componentsMatch(state.current.Component, Component)) {
      // ...switch out StableComponent since hooks will likely mismatch
      state.current.StableComponent = makeStableComponentWithRef<Ref, P>(state);
    }
    state.current.Component = Component;
    return state.current.StableComponent;
  }
  return null;
}

/** In extremely rare cases, useNestedComponent(cond ? Com1 : Com2) might not work. For these cases, use useConstComponent(cond ? Com1 : Com2) to avoid the lint error. */
export function useConstComponent<T>(Component: T): T {
  return Component;
}
